import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import globalStyles from "../../../assets/styles/global.module.scss";
import style from "../../../assets/styles/componentStyles/orderDetailStyles/orderDetailStyle.module.scss";
import feedbackStyles from "../../../assets/styles/componentStyles/feedbackStyles/feedbackStyle.module.scss";
import { UserContext, UserContextPayload } from '../../../context/userContext';
import agent from '../../../services/api';
import { formatDateAccordingToTimezoneUsingMoment, formatString, getValueOrDefault, thousandSeparator } from '../../../utils/constants';
import ReplayIcon from '@mui/icons-material/Replay';
import GlobalPopUp from '../../utility/globalPopUp';
import OrderDetailPopup from './orderDetailPopup';
import EditIcon from '@mui/icons-material/Edit';
import UpdatePaymentStatusPopup from './updatePaymentStatusPopup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GlobalMenu from '../../globalMenu';
import { globalMenuItemData, orderList } from '../../../models/axiosModel';
import { useOrderDetailStore } from '../../../store/orderDetailStore';
import { useShallow } from 'zustand/react/shallow';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getPaymentFilterFlags, ORDER_TABLE_COLUMN_COUNT, ORDER_TABLE_SKELETON_ROW_COUNT } from './orderDetailsUtils';
import { useGenericStore } from '../../../store/genericStore';

interface OrderDetailsTableProps {
    getOrderListApiCall: (start: number, limit: number, searchText?: string, flag_paid_only?: boolean, flag_not_paid_only?: boolean) => Promise<void>,
    searchQuery: string,
    selectedFilter: string,
    isOrderListLoading: boolean,
}

const OrderDetailsTable: React.FC<OrderDetailsTableProps> = (props) => {

    const { getOrderListApiCall, searchQuery, selectedFilter, isOrderListLoading } = props;
    const { flag_paid_only, flag_not_paid_only } = getPaymentFilterFlags(selectedFilter);

    const { orderList, setOrderList, getOrderDetailApi, totalFilteredOrders, setTotalFilteredOrders, setTotalOrders, setTotalPaidOrders, setTotalNotPaidOrders, totalPaidOrders, totalNotPaidOrders, limit, setLimit } = useOrderDetailStore(useShallow((state) => ({
        orderList: state.orderList,
        setOrderList: state.setOrderList,
        getOrderDetailApi: state.getOrderDetailApi,
        totalFilteredOrders: state.totalFilteredOrders,
        setTotalFilteredOrders: state.setTotalFilteredOrders,
        setTotalOrders: state.setTotalOrders,
        setTotalPaidOrders: state.setTotalPaidOrders,
        setTotalNotPaidOrders: state.setTotalNotPaidOrders,
        totalPaidOrders: state.totalPaidOrders,
        totalNotPaidOrders: state.totalNotPaidOrders,
        limit: state.limit,
        setLimit: state.setLimit
    })))

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))
    
    const { setFlagLoading, handleError, userDetail } = useContext(UserContext) as UserContextPayload
    const innerHeight = (window.innerHeight - 390);

    const [page, setPage] = useState<number>(0);
    // const [rowsPerPage, setRowsPerPage] = useState<number>(50);

    const getStartFromPage = (currentPage: number, limit: number) => currentPage * limit + 1;

    useEffect(() => {
        getOrderListApiCall(getStartFromPage(0, limit), limit, undefined, false, false);

        return () => {
            setOrderList(null);
            setLimit(50);
            setTotalFilteredOrders(0);
            setTotalOrders(0);
            setTotalPaidOrders(0);
            setTotalNotPaidOrders(0);
        }
    }, []);

    useEffect(() => {
        setPage(0);
    }, [searchQuery, selectedFilter]);

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
        getOrderListApiCall(getStartFromPage(newPage, limit), limit, searchQuery || undefined, flag_paid_only, flag_not_paid_only);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newLimit = parseInt(event.target.value, 10);
        setLimit(newLimit);
        setPage(0);
        getOrderListApiCall(getStartFromPage(0, newLimit), newLimit, searchQuery || undefined, flag_paid_only, flag_not_paid_only);
    };

    const [flagShowOrderDataInPopup, setFlagShowOrderDataInPopup] = useState<boolean>(false);
    const [flagUpdatePaymentStatusPopup, setFlagUpdatePaymentStatusPopup] = useState<boolean>(false);
    const [paymentMenuAnchorEl, setPaymentMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [orderMenuAnchorEl, setOrderMenuAnchorEl] = useState<null | Element>(null);
    const [selectedOrder, setSelectedOrder] = useState<orderList | null>(null);

    const breakPoints = {
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)')
    }

    const updateOrderPaymentStatus = async (orderId: string) => {
        const updatedOrderDetails = await getOrderDetailApiCall(orderId);

        const updatedPaymentStatus = (orderList: orderList[] | null) => {
            if(!orderList) return orderList;
            return orderList?.map((orderItem) => {
                if(orderItem?.order_id === orderId){
                    return {
                        ...orderItem,
                        payment_status: updatedOrderDetails?.data?.payment_status ?? orderItem?.payment_status,
                        order_paid_timestamp: updatedOrderDetails?.data?.order_paid_timestamp ?? orderItem?.order_paid_timestamp
                    }
                }
                return orderItem;
            })
        }
        setOrderList(updatedPaymentStatus(orderList));

        if(updatedOrderDetails?.data?.payment_status?.toLowerCase() === "paid"){
            setTotalPaidOrders(totalPaidOrders + 1);
            setTotalNotPaidOrders(totalNotPaidOrders - 1);
        }
    }

    // Check Payment Status API
    const checkPaymentStatusApi = async (order: orderList) => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true }));

        const payload = {
            ecom_order_source_reference_id: order?.order_source_reference_id || "",
            ecom_phone_number: order?.phone_no || "",
        }
        const res = await agent.checkPaymentStatus.post(payload);

        if(!(res.status)){
            handleError(res);
        } else {
            showSuccess("Payment Fetched Successfully.")
            updateOrderPaymentStatus(order?.order_id);
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false }));
    }

    const onClickOfOrderRow = async (order: orderList) => {
        await getOrderDetailApiCall(order?.order_id);
        setFlagShowOrderDataInPopup(true);
    }

    const onClickOfUpdateStatusIcon = (order: orderList) => {
        setFlagUpdatePaymentStatusPopup(true);
        setSelectedOrder(order);
        if(flagShowOrderDataInPopup){
            setFlagShowOrderDataInPopup(false)
        }
    } 

    const onCloseOfUpdateStatusPopup = () => {
        setFlagUpdatePaymentStatusPopup(false);
        setSelectedOrder(null);
        if(flagShowOrderDataInPopup){
            setFlagShowOrderDataInPopup(false)
        }
    }

    const handlePaymentMenuOpen = (event: React.MouseEvent<HTMLElement>, order: orderList) => {
        event.stopPropagation();
        event.preventDefault();
        setOrderMenuAnchorEl(null);
        setSelectedOrder(order);
        setPaymentMenuAnchorEl(event.currentTarget);
    };

    const handlePaymentMenuClose = () => {
        setPaymentMenuAnchorEl(null);
        setSelectedOrder(null);
    };

    const handleOrderMenuOpen = (event: React.MouseEvent<Element>, order: orderList) => {
        event.stopPropagation();
        event.preventDefault();
        setPaymentMenuAnchorEl(null);
        setSelectedOrder(order);
        setOrderMenuAnchorEl(event.currentTarget);
    };

    const handleOrderMenuClose = () => {
        setOrderMenuAnchorEl(null);
        setSelectedOrder(null);
    };

    const handleOrderDetailPopupClose = () => {
        handlePaymentMenuClose();
        handleOrderMenuClose();
        setFlagShowOrderDataInPopup(false);
    };

    const paymentStatusMenuItems: globalMenuItemData[] = [
        {
            type: "item",
            label: "Refresh Status",
            icon: <ReplayIcon fontSize="small" />,
            onClick: (order: orderList) => checkPaymentStatusApi(order),
        },
        {
            type: "item",
            label: "Mark as Paid",
            icon: <EditIcon fontSize="small" />,
            onClick: (order: orderList) => onClickOfUpdateStatusIcon(order),
        },
    ];

    const orderMenuItems: globalMenuItemData[] = [
        {
            type: "item",
            label: "View Order Detail",
            icon: <VisibilityIcon fontSize="small" />,
            onClick: (order: orderList) => onClickOfOrderRow(order),
        },
    ];

    const renderPaymentStatusBadge = (order: orderList | null, paymentStatus: string) => {
        if(!order) return "-";
        const isPaid = paymentStatus === "Paid";

        if (isPaid) {
            return (
                <div className={`${style.paymentStatusBadge} ${style.paymentStatusBadgePaid}`}>
                    <span className={`${style.paymentStatusDot} ${style.paymentStatusDotPaid}`} />
                    Paid
                </div>
            );
        }

        return (
            <div className={`${style.paymentStatusBadge} ${style.paymentStatusBadgeNotPaid}`} onClick={(e) => handlePaymentMenuOpen(e, order)}>
                <span className={`${style.paymentStatusDot} ${style.paymentStatusDotNotPaid}`} />
                Unpaid
                <span className={style.paymentStatusArrow}>
                    <KeyboardArrowDownIcon />
                </span>
            </div>
        );
    };

    const renderOrderTableColGroup = () => (
        <colgroup>
            <col style={{ width: "160px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "70px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "38px" }} />
        </colgroup>
    );

    const renderTableSkeletonRows = () => {
        return Array.from({ length: ORDER_TABLE_SKELETON_ROW_COUNT }).map((_, rowIndex) => (
            <TableRow key={`order-skeleton-row-${rowIndex}`}>
                {Array.from({ length: ORDER_TABLE_COLUMN_COUNT }).map((_, cellIndex) => {
                    const isActionCell = cellIndex === ORDER_TABLE_COLUMN_COUNT - 1;

                    return (
                        <TableCell
                            key={`order-skeleton-cell-${rowIndex}-${cellIndex}`}
                            className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${isActionCell ? `${style.orderDetailsTableActionCell} ${style.orderDetailsSkeletonActionCell}` : `${style.orderDetailsTableCell} ${style.orderDetailsSkeletonCell}`}`}
                        >
                            <Skeleton
                                variant={isActionCell ? "circular" : "text"}
                                width={isActionCell ? 24 : "100%"}
                                height={24}
                                animation="wave"
                            />
                        </TableCell>
                    );
                })}
            </TableRow>
        ));
    };

    // Get Order Detail API
    const getOrderDetailApiCall = async (ecom_order_id: string) => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true }));
        const organizationId = userDetail?.user_organization_data?.organization_id ?? 0;
        const res = await getOrderDetailApi(organizationId, ecom_order_id);
        if(!(res?.status)){
            handleError(res);
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false }));
        return res;
    }

    return (
        <>
            <Grid container className={`${globalStyles.flexColumn} ${globalStyles.gapTwo}`}>
                <Grid item className={globalStyles.fullwidth}>
                    <TableContainer
                        className={`${globalStyles.WritewizGlobaltable} ${style.orderDetailsTableContainer}`}
                        sx={{ maxHeight: `${innerHeight}px !important`, overflow: 'auto', position: "relative" }}
                    >
                        <Table
                            aria-label="order_detail_table"
                            stickyHeader
                            className={style.orderDetailsTable}
                        >
                            {renderOrderTableColGroup()}
                            <TableHead>
                                <TableRow>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Buyer Name</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Phone Number</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Reference ID</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Received from</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>#Items</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Order At</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Total Amount</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableBadgeCell}`}>Payment Status</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.orderDetailsTableCell}`}>Paid At</TableCell>
                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.orderDetailsTableActionCell}`}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody key={`order-table-page-${page}`}>
                                {isOrderListLoading ? (
                                    renderTableSkeletonRows()
                                ) : orderList?.length ? (
                                    orderList?.map((order: orderList) => {
                                        const paymentStatus: string = formatString(order?.payment_status);
                                        const amount = `${order?.currency} ${thousandSeparator(String(Number(order?.total_amount).toFixed(2)))}`;
                                        const buyerName = getValueOrDefault(order?.buyer_name);
                                        const referenceId = getValueOrDefault(order?.order_source_reference_id);
                                        const receivedFrom = getValueOrDefault(order?.whatsapp_phone_number);
                                        const orderAt = formatDateAccordingToTimezoneUsingMoment(order?.order_created_timestamp, userDetail.timezone_hour);
                                        const paidAt = order?.order_paid_timestamp ? formatDateAccordingToTimezoneUsingMoment(order?.order_paid_timestamp, userDetail.timezone_hour) : "-";

                                        return (
                                            <TableRow key={`${order?.order_id}-${order?.order_source_reference_id}`} className={`${dashboardStyles.cellRow} ${style.tableRow}`} onClick={() => onClickOfOrderRow(order)}>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {buyerName}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {order?.phone_no}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {referenceId}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {receivedFrom}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {order?.item_count ?? 0}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {orderAt}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`} >
                                                    {amount}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableBadgeCell}`}>
                                                    {renderPaymentStatusBadge(order, paymentStatus)}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.orderDetailsTableCell}`}>
                                                    {paidAt}
                                                </TableCell>
                                                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon} ${style.orderDetailsTableActionCell}`}>
                                                    <MoreVertIcon
                                                        className={globalStyles.clickable}
                                                        onClick={(e) => handleOrderMenuOpen(e, order)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow className={dashboardStyles.cellRow}>
                                        <TableCell colSpan={ORDER_TABLE_COLUMN_COUNT} className={globalStyles.textCenter}>
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody> 
                        </Table>
                    </TableContainer>
                </Grid>

                {totalFilteredOrders > 0 && !isOrderListLoading &&
                    <Grid item className={globalStyles.textItemsEnd}>
                        <Grid container className={globalStyles.contentEnd}>
                            <Grid item className={style.orderDetailsTablePaginationGrid}>
                                <TablePagination
                                    component="div"
                                    className={`${feedbackStyles.feedbackTablePaginationStyle} ${style.orderDetailsTablePagination}`}
                                    count={totalFilteredOrders}
                                    rowsPerPageOptions={[10, 25, 50, 100, 1000]}
                                    page={page}
                                    rowsPerPage={limit}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    // labelDisplayedRows={({ from, to, count }) => `Records: ${from}-${to} of ${count}`}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
            
            {flagShowOrderDataInPopup && 
                <GlobalPopUp
                    open={flagShowOrderDataInPopup}
                    onClose={handleOrderDetailPopupClose}
                    handleClose={handleOrderDetailPopupClose}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "310px" : "850px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "950px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <OrderDetailPopup
                        renderPaymentStatusBadge={renderPaymentStatusBadge}
                        getOrderDetailApiCall={getOrderDetailApiCall}
                    />
                    <GlobalMenu
                        anchorEl={paymentMenuAnchorEl}
                        handleMenuClose={handlePaymentMenuClose}
                        menuItemData={paymentStatusMenuItems}
                        open={Boolean(paymentMenuAnchorEl)}
                        additionalField={selectedOrder}
                        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    />
                </GlobalPopUp> 
            }

            <GlobalMenu
                anchorEl={paymentMenuAnchorEl}
                handleMenuClose={handlePaymentMenuClose}
                menuItemData={paymentStatusMenuItems}
                open={Boolean(paymentMenuAnchorEl) && !flagShowOrderDataInPopup}
                additionalField={selectedOrder}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            />

            <GlobalMenu
                anchorEl={orderMenuAnchorEl}
                handleMenuClose={handleOrderMenuClose}
                menuItemData={orderMenuItems}
                open={Boolean(orderMenuAnchorEl)}
                additionalField={selectedOrder}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            />

            {flagUpdatePaymentStatusPopup && 
                <GlobalPopUp
                    open={flagUpdatePaymentStatusPopup}
                    flagShowCloseIcon
                    handleClose={() => onCloseOfUpdateStatusPopup()}
                    onClose={() => onCloseOfUpdateStatusPopup()}
                    paperMinWidth={breakPoints.flagSmallScreen ? "310px" : "600px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "650px"}
                    borderRadius='10px'
                    titlePadding='0px'
                >
                    <UpdatePaymentStatusPopup
                        selectedOrder={selectedOrder}
                        onCloseOfUpdateStatusPopup={onCloseOfUpdateStatusPopup}
                        updateOrderPaymentStatus={updateOrderPaymentStatus}
                    />
                </GlobalPopUp>
            }
        </>
    )
}

export default OrderDetailsTable
